import ReactGA from 'react-ga4';
import mixpanel from 'mixpanel-browser';
import { config } from '@/config/environment';

// Analytics initialization
export const initAnalytics = () => {
  // Initialize Google Analytics
  if (config.enableAnalytics && config.gaTrackingId) {
    ReactGA.initialize(config.gaTrackingId, {
      gaOptions: {
        siteSpeedSampleRate: 100,
      },
    });
    console.log('Google Analytics initialized');
  }

  // Initialize Mixpanel
  if (config.enableAnalytics && config.mixpanelToken) {
    mixpanel.init(config.mixpanelToken, {
      debug: config.env === 'development',
      track_pageview: true,
      persistence: 'localStorage',
    });
    console.log('Mixpanel initialized');
  }
};

// Page view tracking
export const trackPageView = (path: string, title?: string) => {
  if (!config.enableAnalytics) return;

  // Google Analytics
  if (config.gaTrackingId) {
    ReactGA.send({ hitType: 'pageview', page: path, title });
  }

  // Mixpanel
  if (config.mixpanelToken) {
    mixpanel.track('Page View', {
      page: path,
      title: title || document.title,
    });
  }
};

// Event tracking
export const trackEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number
) => {
  if (!config.enableAnalytics) return;

  // Google Analytics
  if (config.gaTrackingId) {
    ReactGA.event({
      category,
      action,
      label,
      value,
    });
  }

  // Mixpanel
  if (config.mixpanelToken) {
    mixpanel.track(`${category} - ${action}`, {
      label,
      value,
    });
  }
};

// User identification
export const identifyUser = (userId: string, properties?: Record<string, any>) => {
  if (!config.enableAnalytics) return;

  // Google Analytics
  if (config.gaTrackingId) {
    ReactGA.set({ userId });
  }

  // Mixpanel
  if (config.mixpanelToken) {
    mixpanel.identify(userId);
    if (properties) {
      mixpanel.people.set(properties);
    }
  }
};

// User properties
export const setUserProperties = (properties: Record<string, any>) => {
  if (!config.enableAnalytics) return;

  // Google Analytics
  if (config.gaTrackingId) {
    ReactGA.set(properties);
  }

  // Mixpanel
  if (config.mixpanelToken) {
    mixpanel.people.set(properties);
  }
};

// Conversion tracking
export const trackConversion = (
  conversionName: string,
  value?: number,
  currency: string = 'USD'
) => {
  if (!config.enableAnalytics) return;

  // Google Analytics
  if (config.gaTrackingId) {
    ReactGA.event('conversion', {
      event_category: 'Conversion',
      event_label: conversionName,
      value,
      currency,
    });
  }

  // Mixpanel
  if (config.mixpanelToken) {
    mixpanel.track('Conversion', {
      name: conversionName,
      value,
      currency,
    });
  }
};

// Timing tracking
export const trackTiming = (
  category: string,
  variable: string,
  value: number,
  label?: string
) => {
  if (!config.enableAnalytics) return;

  // Google Analytics
  if (config.gaTrackingId) {
    ReactGA.event('timing_complete', {
      name: variable,
      value,
      event_category: category,
      event_label: label,
    });
  }

  // Mixpanel
  if (config.mixpanelToken) {
    mixpanel.track('Timing', {
      category,
      variable,
      value,
      label,
    });
  }
};

// Predefined event helpers
export const analytics = {
  // Screen tracking
  screen: (screenName: string) => {
    trackPageView(screenName);
  },

  // User actions
  buttonClick: (buttonName: string, location: string) => {
    trackEvent('User Interaction', 'Button Click', `${buttonName} - ${location}`);
  },

  featureUsage: (featureName: string) => {
    trackEvent('Feature Usage', featureName);
  },

  // Authentication
  login: (method: string) => {
    trackEvent('Authentication', 'Login', method);
  },

  logout: () => {
    trackEvent('Authentication', 'Logout');
  },

  signup: (method: string) => {
    trackEvent('Authentication', 'Signup', method);
    trackConversion('Signup Complete');
  },

  // Activities
  activityLogged: (activityType: string) => {
    trackEvent('Activity', 'Log Activity', activityType);
  },

  photoUploaded: (category: string) => {
    trackEvent('Photo', 'Upload Photo', category);
  },

  voiceCommandUsed: (command: string) => {
    trackEvent('Voice', 'Voice Command', command);
  },

  // Gamification
  achievementUnlocked: (achievementName: string) => {
    trackEvent('Gamification', 'Achievement Unlocked', achievementName);
  },

  scoreUpdated: (newScore: number) => {
    trackEvent('Gamification', 'Score Updated', undefined, newScore);
  },

  // Network
  networkAction: (action: string) => {
    trackEvent('Network', action);
  },

  // Satellite
  satelliteDataViewed: (dataType: string) => {
    trackEvent('Satellite', 'View Data', dataType);
  },

  // Analytics
  reportGenerated: (reportType: string) => {
    trackEvent('Analytics', 'Generate Report', reportType);
  },

  // PWA
  pwaInstalled: () => {
    trackEvent('PWA', 'Install');
    trackConversion('PWA Install');
  },

  // Offline
  offlineAction: (action: string) => {
    trackEvent('Offline', action);
  },

  syncCompleted: (itemCount: number) => {
    trackEvent('Offline', 'Sync Completed', undefined, itemCount);
  },

  // Errors
  error: (errorType: string, errorMessage: string) => {
    trackEvent('Error', errorType, errorMessage);
  },
};
